// utils/selfPacedApi.js
// ──────────────────────────────────────────────────────────────────────────────
// Centralised API helper for all self-paced / LXP endpoints.
// Every function returns the axios promise so callers can await / .then().
// ──────────────────────────────────────────────────────────────────────────────

import apiClient from './apiClient';

const SP = '/self-paced';

/**
 * Every row of a paged `{ data, pagination }` self-paced list. These endpoints
 * default to 12–50 rows, and a screen that filters or counts in the browser
 * silently lost the rest.
 */
export const fetchAllPages = async (fetchPage, params = {}, limit = 200) => {
  const first = await fetchPage({ ...params, page: 1, limit });
  const rows = [...(first.data?.data || [])];
  const pages = Math.min(first.data?.pagination?.pages || 1, 50);
  for (let page = 2; page <= pages; page += 1) {
    const res = await fetchPage({ ...params, page, limit });
    rows.push(...(res.data?.data || []));
  }
  return rows;
};

// ═══════════════════════════════════════════════════════════════════════════════
// PUBLIC CATALOG
// ═══════════════════════════════════════════════════════════════════════════════

export const listPublicCourses = (params = {}) =>
  apiClient.get(`${SP}/public/courses`, { params });

export const getPublicCourse = (idOrSlug) =>
  apiClient.get(`${SP}/public/courses/${idOrSlug}`);

export const verifyCertificate = (certNo) =>
  apiClient.get(`${SP}/certificates/verify/${certNo}`);

// ═══════════════════════════════════════════════════════════════════════════════
// ENROLLMENT (student)
// ═══════════════════════════════════════════════════════════════════════════════

export const purchaseCourse = (courseId) =>
  apiClient.post(`${SP}/enroll/purchase`, { courseId });

export const verifyPurchase = (body) =>
  apiClient.post(`${SP}/enroll/verify`, body);

export const getMyEnrollments = () =>
  apiClient.get(`${SP}/enroll/my`);

export const adminGrantEnrollment = (body) =>
  apiClient.post(`${SP}/enroll/admin-grant`, body);

// ═══════════════════════════════════════════════════════════════════════════════
// INSTRUCTOR (trainer) teaching library — self-paced courses assigned to them
// ═══════════════════════════════════════════════════════════════════════════════

export const getInstructorSelfPacedCourses = () =>
  apiClient.get(`${SP}/instructor/my-courses`);

/** Trainer — learner roster + completion for an assigned self-paced course */
export const getInstructorCourseProgress = (courseId, params = {}) =>
  apiClient.get(`${SP}/instructor/courses/${courseId}/progress`, { params });

/** Trainer — per-module / per-lesson breakdown for one learner */
export const getInstructorStudentProgress = (courseId, studentId) =>
  apiClient.get(`${SP}/instructor/courses/${courseId}/students/${studentId}/progress`);

// ═══════════════════════════════════════════════════════════════════════════════
// THUMBNAIL UPLOAD
// ═══════════════════════════════════════════════════════════════════════════════

export const getCourseThumbnailUploadUrl = (courseId, body) =>
  apiClient.post(`${SP}/courses/${courseId}/thumbnail`, body, body instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined);

// ═══════════════════════════════════════════════════════════════════════════════
// MODULE CRUD (admin)
// ═══════════════════════════════════════════════════════════════════════════════

export const createModule = (body) =>
  apiClient.post(`${SP}/modules`, body);

export const updateModule = (id, body) =>
  apiClient.put(`${SP}/modules/${id}`, body);

export const deleteModule = (id) =>
  apiClient.delete(`${SP}/modules/${id}`);

// ═══════════════════════════════════════════════════════════════════════════════
// LESSON CRUD (admin)
// ═══════════════════════════════════════════════════════════════════════════════

export const createLesson = (body, opts = {}) =>
  apiClient.post(`${SP}/lessons`, body, {
    ...(body instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {}),
    onUploadProgress: opts.onUploadProgress,
  });

export const confirmLessonUpload = (id) =>
  apiClient.post(`${SP}/lessons/${id}/confirm-upload`);

export const updateLesson = (id, body) =>
  apiClient.put(`${SP}/lessons/${id}`, body);

export const deleteLesson = (id) =>
  apiClient.delete(`${SP}/lessons/${id}`);

// ═══════════════════════════════════════════════════════════════════════════════
// CONTENT DELIVERY
// ═══════════════════════════════════════════════════════════════════════════════

export const getCurriculum = (courseId) =>
  apiClient.get(`${SP}/courses/${courseId}/curriculum`);

export const getLessonContent = (lessonId) =>
  apiClient.get(`${SP}/lessons/${lessonId}/content`);

export const streamLessonVideo = (lessonId) =>
  apiClient.get(`${SP}/lessons/${lessonId}/stream`);

// ═══════════════════════════════════════════════════════════════════════════════
// PROGRESS (student)
// ═══════════════════════════════════════════════════════════════════════════════

export const updateLessonProgress = (lessonId, body) =>
  apiClient.patch(`${SP}/lessons/${lessonId}/progress`, body);

export const getCourseProgress = (courseId) =>
  apiClient.get(`${SP}/courses/${courseId}/progress`);

// ═══════════════════════════════════════════════════════════════════════════════
// QUIZ
// ═══════════════════════════════════════════════════════════════════════════════

export const createQuiz = (body) =>
  apiClient.post(`${SP}/quiz`, body);

export const updateQuiz = (id, body) =>
  apiClient.put(`${SP}/quiz/${id}`, body);

export const deleteQuiz = (id) =>
  apiClient.delete(`${SP}/quiz/${id}`);

export const getQuiz = (quizId) =>
  apiClient.get(`${SP}/quiz/${quizId}`);

export const startQuiz = (quizId) =>
  apiClient.post(`${SP}/quiz/${quizId}/start`);

export const submitQuiz = (quizId, body) =>
  apiClient.post(`${SP}/quiz/${quizId}/submit`, body);

// ═══════════════════════════════════════════════════════════════════════════════
// CERTIFICATES
// ═══════════════════════════════════════════════════════════════════════════════

export const getMyCertificates = () =>
  apiClient.get(`${SP}/certificates/my`);

export const getCertificate = (courseId) =>
  apiClient.get(`${SP}/certificates/${courseId}`);

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN: Enrollment analytics
// ═══════════════════════════════════════════════════════════════════════════════

export const getEnrollmentsByCourse = (courseId, params = {}) =>
  apiClient.get(`${SP}/admin/courses/${courseId}/enrollments`, { params });

/** Admin — per-module / per-lesson breakdown for one learner */
export const getStudentProgressDetail = (courseId, studentId) =>
  apiClient.get(`${SP}/admin/courses/${courseId}/students/${studentId}/progress`);

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN: Certificate Template Settings
// ═══════════════════════════════════════════════════════════════════════════════

export const getCertificateSettings = () =>
  apiClient.get(`${SP}/certificate-settings`);

export const updateCertificateSettings = (data) =>
  apiClient.put(`${SP}/certificate-settings`, data);

export const getUploadBackgroundUrl = (body) =>
  apiClient.post(`${SP}/certificate-settings/upload-background`, body, body instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined);

export const uploadCertificateLogo = (body) =>
  apiClient.post(`${SP}/certificate-settings/upload-logo`, body, { headers: { 'Content-Type': 'multipart/form-data' } });

export const uploadCertificateStamp = (body) =>
  apiClient.post(`${SP}/certificate-settings/upload-stamp`, body, { headers: { 'Content-Type': 'multipart/form-data' } });

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN: Certificate Issuance
// ═══════════════════════════════════════════════════════════════════════════════

export const adminListSelfPacedCourses = () =>
  apiClient.get(`${SP}/admin/courses/list`);

export const adminGetCertificates = (params = {}) =>
  apiClient.get(`${SP}/admin/certificates`, { params });

export const adminIssueCertificate = (body) =>
  apiClient.post(`${SP}/admin/certificates/issue`, body);

export const adminBulkIssueCertificates = (body) =>
  apiClient.post(`${SP}/admin/certificates/issue-bulk`, body);

export const adminPreviewCertificatePdf = (body) =>
  apiClient.post(`${SP}/admin/certificates/preview-pdf`, body, { responseType: 'blob' });

// ═══════════════════════════════════════════════════════════════════════════════
// COURSE CRUD — uses the existing /courses endpoints with courseType param
// ═══════════════════════════════════════════════════════════════════════════════

export const getSelfPacedCourses = (params = {}) =>
  apiClient.get('/courses/list', { params: { ...params, courseType: 'self-paced', q: 'info' } });

export const getSelfPacedCourse = (id) =>
  apiClient.get(`/courses/${id}`);

export const addSelfPacedCourse = (body) =>
  apiClient.post('/courses/add', { ...body, courseType: 'self-paced' });

export const updateSelfPacedCourse = (id, body) =>
  apiClient.put(`/courses/${id}`, body);

export const deleteSelfPacedCourse = (id) =>
  apiClient.delete(`/courses/delete/${id}`);

// ═══════════════════════════════════════════════════════════════════════════════
// DOCUMENT AI EXPLANATIONS
// ═══════════════════════════════════════════════════════════════════════════════

const DOC_AI = '/documents';

/** Admin — trigger AI explanation generation for a lesson */
export const triggerAIExplanation = (lessonId) =>
  apiClient.post(`${DOC_AI}/trigger-ai`, { lessonId });

/** Admin — retry failed AI explanation for a lesson */
export const retryAIExplanation = (lessonId) =>
  apiClient.post(`${DOC_AI}/${lessonId}/retry`);

/** Admin/Student — get AI processing status for a lesson */
export const getAIStatus = (lessonId) =>
  apiClient.get(`${DOC_AI}/${lessonId}/status`);

/** Admin — get bulk AI status for multiple lessons */
export const getBulkAIStatus = (lessonIds) =>
  apiClient.post(`${DOC_AI}/bulk-status`, { lessonIds });

/** Student/Admin — get AI explanations for a lesson (paginated) */
export const getAIExplanations = (lessonId, params = {}) =>
  apiClient.get(`${DOC_AI}/${lessonId}/explanations`, { params });

/** Student/Admin — get parsed document pages for a lesson */
export const getDocumentPages = (lessonId) =>
  apiClient.get(`${DOC_AI}/${lessonId}/pages`);

/** Admin — get AI settings for the institute */
export const getAISettings = () =>
  apiClient.get(`${DOC_AI}/ai-settings`);

/** Admin — update AI settings for the institute */
export const updateAISettings = (body) =>
  apiClient.put(`${DOC_AI}/ai-settings`, body);

/** Admin — get AI usage stats for the institute */
export const getAIUsage = () =>
  apiClient.get(`${DOC_AI}/ai-usage`);

/** Admin — get BullMQ queue metrics */
export const getQueueMetrics = () =>
  apiClient.get(`${DOC_AI}/queue-metrics`);

// ═══════════════════════════════════════════════════════════════════════════════
// LIVE COURSE (BATCH) CERTIFICATES
// ═══════════════════════════════════════════════════════════════════════════════

/** Get all students in a batch plus their certificate status */
export const getBatchStudentsForCert = (batchId) =>
  apiClient.get(`/batches/${batchId}/students-for-cert`);

/** Get all issued certificates for a batch */
export const getBatchCertificates = (batchId) =>
  apiClient.get(`/batches/${batchId}/certificates`);

/** Issue a certificate for a single student in a batch */
export const issueBatchCertificate = (batchId, body) =>
  apiClient.post(`/batches/${batchId}/certificates/issue`, body);

/** Issue certificates for all students in a batch that don't have one yet */
export const issueBatchCertificatesBulk = (batchId) =>
  apiClient.post(`/batches/${batchId}/certificates/issue-bulk`, {});
